import { Button } from '@aod/ui/components/ui/button';
import { useCallback, useEffect, useState } from 'react';
import { getGatewayHttpUrl } from '../../auth/serviceToken';
import { useSession } from '../../auth/useSession';
import type { Alignment } from '../types';
import { AlignmentPicker } from './AlignmentPicker';
import { LobbyHero } from './LobbyHero.js';
import { QueuePanel } from './QueuePanel.js';
import { TeamsToggle } from './TeamsToggle.js';
import { WatchField } from './WatchField.js';
import { LOBBY_PRESETS } from './lobbyPresets';
import type { UseMpLobbyReturn } from './useMpLobby';

/** Text input on the dark lobby card (amber-on-ink like the menu). */
const INPUT_CLASS =
  'min-h-11 rounded-md border border-amber-200/30 bg-black/40 px-4 py-3 text-amber-50 placeholder:text-amber-100/60 focus-visible:border-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50';

const AI_DIFFICULTIES: Array<'easy' | 'normal' | 'hard'> = ['easy', 'normal', 'hard'];
type QueueTab = 'ranked' | 'quickplay' | 'custom';

interface Props {
  mp: UseMpLobbyReturn;
  localPlayerName: string;
  onSetPlayerName: (name: string) => void;
  /** Leave the lobby screen for the start menu (shown while not inside a lobby). */
  onBack?: () => void;
  /** Open the read-only viewer on a running match (a match id or watch link). */
  onWatch?: (matchId: string) => void;
  /** Gateway ws:// URL the hook connects to — used to resolve the http:// origin for the rating API. */
  gatewayUrl?: string;
}

export function LobbyScreen({
  mp,
  localPlayerName,
  onSetPlayerName,
  onBack,
  onWatch,
  gatewayUrl,
}: Props) {
  const [inviteInput, setInviteInput] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [aiName, setAiName] = useState('AI');
  const [aiDifficulty, setAiDifficulty] = useState<'easy' | 'normal' | 'hard'>('normal');
  const [aiFogOfWar, setAiFogOfWar] = useState(true);
  const [selectedAlignment, setSelectedAlignment] = useState<Alignment | null>(null);
  const [tab, setTab] = useState<QueueTab>('ranked');

  const localPlayerId = mp.getCurrentPlayerId() ?? '';

  const handleCreate = useCallback(() => {
    if (!localPlayerName.trim()) return;
    mp.createLobby(localPlayerName.trim());
  }, [localPlayerName, mp]);

  const handleJoin = useCallback(() => {
    if (!inviteInput.trim() || !localPlayerName.trim()) return;
    mp.joinLobby(inviteInput.trim().toUpperCase(), localPlayerName.trim());
  }, [inviteInput, localPlayerName, mp]);

  const handleSendChat = useCallback(() => {
    if (!chatInput.trim()) return;
    mp.sendChat(localPlayerName, chatInput.trim());
    setChatInput('');
  }, [chatInput, localPlayerName, mp]);

  const handleAddAi = useCallback(() => {
    const name = aiName.trim() || 'AI';
    mp.addAiSlot(name, aiDifficulty, aiFogOfWar);
  }, [aiName, aiDifficulty, aiFogOfWar, mp]);

  const handleSetAlignment = useCallback(
    (alignment: Alignment) => {
      setSelectedAlignment(alignment);
      mp.sendMessage({ type: 'lobby:set-alignment', playerId: localPlayerId, alignment });
    },
    [localPlayerId, mp],
  );

  const lobby = mp.lobby;

  // No lobby yet — tabs: Ranked | Quickplay | Custom (create/join)
  if (!lobby) {
    return (
      <main className="flex h-full w-full items-center justify-center overflow-y-auto bg-gradient-to-b from-[#0d0f1c] to-[#060810] p-4 text-amber-50">
        <div className="flex w-96 max-w-full flex-col gap-5 rounded-2xl border border-amber-200/25 bg-black/40 p-6 shadow-2xl backdrop-blur sm:p-8">
          <h1 className="bg-gradient-to-b from-[#e8d5a0] to-[#c49b4c] bg-clip-text text-2xl font-extrabold tracking-wide text-transparent">
            Ages of Dominion — Multiplayer
          </h1>
          <div
            className="flex gap-1 rounded-lg bg-white/5 p-1"
            role="tablist"
            aria-label="Game type"
          >
            {(['ranked', 'quickplay', 'custom'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                data-testid={`mp-tab-${t}`}
                className={`min-h-9 flex-1 rounded-md px-3 py-1.5 text-sm capitalize transition-colors ${
                  tab === t
                    ? 'bg-amber-400 font-bold text-amber-950'
                    : 'border border-amber-200/30 text-amber-50 hover:bg-white/10'
                }`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {tab !== 'ranked' && (
            <input
              className={INPUT_CLASS}
              placeholder="Your name"
              aria-label="Your name"
              value={localPlayerName}
              onChange={(e) => onSetPlayerName(e.target.value)}
            />
          )}

          {tab === 'custom' && (
            <div className="flex flex-col gap-4">
              <Button
                type="button"
                size="lg"
                onClick={handleCreate}
                disabled={!localPlayerName.trim()}
              >
                Create Lobby
              </Button>
              <div className="flex gap-2">
                <input
                  className={`${INPUT_CLASS} min-w-0 flex-1 uppercase`}
                  placeholder="Invite Code"
                  aria-label="Invite code"
                  value={inviteInput}
                  onChange={(e) => setInviteInput(e.target.value)}
                  maxLength={6}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={handleJoin}
                  disabled={!inviteInput.trim() || !localPlayerName.trim()}
                >
                  Join
                </Button>
              </div>
            </div>
          )}

          {onWatch && tab === 'custom' && <WatchField onWatch={onWatch} />}

          {tab !== 'custom' && (
            <QueuePanel
              mp={mp}
              ranked={tab === 'ranked'}
              localPlayerName={localPlayerName}
              gatewayUrl={gatewayUrl}
            />
          )}
          {mp.phase === 'connecting' && (
            <p className="text-xs text-amber-100/80" aria-live="polite" data-testid="mp-connecting">
              Connecting to the multiplayer server… If this never finishes the server is offline;
              single-player still works.
            </p>
          )}
          {onBack && (
            <button
              type="button"
              className="inline-flex min-h-9 items-center self-start text-sm text-amber-100 underline underline-offset-4 hover:text-white"
              data-testid="mp-back-to-menu"
              onClick={onBack}
            >
              Back to menu
            </button>
          )}
        </div>
      </main>
    );
  }

  const totalPlayers = lobby.players.length + lobby.aiSlots.length;
  const readyPlayers = lobby.players.filter((p) => p.ready).length;
  const allPlayersReady = lobby.players.length > 0 && lobby.players.every((p) => p.ready);
  const canStart = totalPlayers >= 2 && allPlayersReady;

  return (
    <div className="flex h-full w-full bg-gray-900 text-white">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8">
        <h1 className="text-2xl font-bold">Lobby</h1>
        <div className="rounded-lg bg-gray-800 p-4">
          <p className="mb-2 text-sm text-gray-400">Invite Code</p>
          <p className="font-mono text-3xl tracking-widest">{lobby.inviteCode}</p>
        </div>
        <div className="w-80 rounded-lg bg-gray-800 p-4">
          <p className="mb-2 text-sm text-gray-400">
            Players ({totalPlayers}/{lobby.config.maxPlayers})
          </p>
          <LobbyHero partySize={totalPlayers} />
          <ul className="space-y-1">
            {lobby.players.map((p) => (
              <li key={p.accountId} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${p.ready ? 'bg-green-400' : 'bg-gray-500'}`}
                  />
                  <span>{p.displayName}</span>
                  {lobby.isHost && lobby.players[0]?.accountId === p.accountId && (
                    <span className="ml-auto text-xs text-yellow-400">Host</span>
                  )}
                </div>
                {p.accountId === localPlayerId && (
                  <AlignmentPicker selected={selectedAlignment} onSelect={handleSetAlignment} />
                )}
                {!lobby.isHost && (
                  <button
                    type="button"
                    onClick={p.ready ? mp.setUnready : mp.setReady}
                    className={`ml-auto rounded px-2 py-0.5 text-xs ${
                      p.ready ? 'bg-green-700 text-white' : 'bg-gray-600 text-white'
                    }`}
                  >
                    {p.ready ? 'Ready!' : 'Ready'}
                  </button>
                )}
              </li>
            ))}
            {lobby.aiSlots.map((ai) => (
              <li key={ai.slotId} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-green-400" />
                <span>{ai.displayName}</span>
                <span className="ml-auto text-xs text-purple-300">{ai.difficulty}</span>
                {!ai.aiFogOfWar && (
                  <span className="text-xs text-yellow-400" title="Fog of War disabled">
                    ☁
                  </span>
                )}
                {lobby.isHost && (
                  <button
                    type="button"
                    className="ml-1 text-xs text-red-400 hover:text-red-300"
                    onClick={() => mp.removeAiSlot(ai.slotId)}
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Host controls: AI slots + config */}
        {lobby.isHost && (
          <div className="flex w-80 flex-col gap-3 rounded-lg bg-gray-800 p-4">
            <p className="text-sm text-gray-400">Add AI Player</p>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded bg-gray-700 px-2 py-1 text-sm text-white"
                placeholder="AI name"
                value={aiName}
                onChange={(e) => setAiName(e.target.value)}
              />
              <select
                className="rounded bg-gray-700 px-2 py-1 text-sm text-white"
                value={aiDifficulty}
                onChange={(e) => setAiDifficulty(e.target.value as 'easy' | 'normal' | 'hard')}
              >
                {AI_DIFFICULTIES.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-xs text-gray-300">
                <input
                  type="checkbox"
                  checked={aiFogOfWar}
                  onChange={(e) => setAiFogOfWar(e.target.checked)}
                  className="accent-purple-500"
                />
                FoW
              </label>
              <button
                type="button"
                className="rounded bg-purple-600 px-3 py-1 text-sm hover:bg-purple-500 disabled:opacity-50"
                onClick={handleAddAi}
                disabled={totalPlayers >= lobby.config.maxPlayers}
              >
                +AI
              </button>
            </div>
          </div>
        )}

        {lobby.isHost && (
          <div className="flex w-80 flex-col gap-3 rounded-lg bg-gray-800 p-4">
            <p className="text-sm text-gray-400">Match Format</p>
            <select
              className="rounded bg-gray-700 px-2 py-1 text-sm text-white"
              value={lobby.config.format ?? 'standard_ffa'}
              onChange={(e) =>
                mp.updateConfig(
                  lobby.config.maxPlayers,
                  lobby.config.mapSize as 'small' | 'medium' | 'large',
                  e.target.value,
                  lobby.config.presetId,
                )
              }
            >
              <option value="blitz_ffa">Blitz (Fast, 2–4p)</option>
              <option value="standard_2v2">Standard 2v2</option>
              <option value="standard_ffa">Standard (FFA, 2–4p)</option>
              <option value="grand">Grand (4p, Full Game)</option>
            </select>
            <p className="text-sm text-gray-400">Rules</p>
            <select
              className="rounded bg-gray-700 px-2 py-1 text-sm text-white"
              data-testid="lobby-preset"
              value={lobby.config.presetId ?? ''}
              onChange={(e) =>
                mp.updateConfig(
                  lobby.config.maxPlayers,
                  lobby.config.mapSize as 'small' | 'medium' | 'large',
                  lobby.config.format,
                  e.target.value || undefined,
                )
              }
            >
              {!lobby.config.presetId && <option value="">Default rules</option>}
              {LOBBY_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <TeamsToggle config={lobby.config} totalSeats={totalPlayers} mp={mp} />
          </div>
        )}

        {lobby.isHost && (
          <button
            type="button"
            className="rounded bg-green-600 px-6 py-2 font-bold hover:bg-green-500 disabled:opacity-50"
            disabled={!canStart}
            onClick={mp.startMatch}
          >
            {!allPlayersReady
              ? `Waiting for players (${readyPlayers}/${lobby.players.length} ready)`
              : canStart
                ? 'Start Match'
                : 'Need at least 2 players'}
          </button>
        )}
        {!lobby.isHost && (
          <p className="text-sm text-gray-400">Waiting for host to start the match…</p>
        )}
        <button
          type="button"
          className="text-sm text-gray-500 underline hover:text-gray-300"
          onClick={mp.leaveLobby}
        >
          Leave Lobby
        </button>
      </div>

      {/* Chat panel */}
      <div className="flex w-80 flex-col border-l border-gray-700">
        <div className="flex-1 overflow-y-auto p-3">
          {mp.chatHistory.map((msg, i) => (
            <p key={`${msg.timestamp}-${i}`} className="mb-1 text-sm">
              <span className="font-bold text-blue-400">{msg.sender}</span>: {msg.text}
            </p>
          ))}
        </div>
        <div className="flex border-t border-gray-700 p-2">
          <input
            className="flex-1 rounded-l bg-gray-700 px-2 py-1 text-sm text-white outline-none"
            placeholder="Chat…"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
          />
          <button
            type="button"
            className="rounded-r bg-blue-600 px-3 py-1 text-sm hover:bg-blue-500"
            onClick={handleSendChat}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
